"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.notNull = exports.toSnakeCase = exports.isValidDate = exports.equals = exports.Deferred = exports.sleep = exports.sortByStreamId = void 0;
exports.compareSimple = compareSimple;
exports.compare = compare;
exports.sort = sort;
exports.computeNextBackoffInterval = computeNextBackoffInterval;
exports.withTimeout = withTimeout;
exports.orTimeout = orTimeout;
exports.retry = retry;
exports.equalsMap = equalsMap;
exports.ageSeconds = ageSeconds;
exports.awaitCondition = awaitCondition;
exports.isValidUuid = isValidUuid;
exports.isValidUuidV4 = isValidUuidV4;
exports.clone = clone;
exports.omitProperties = omitProperties;
exports.applianceCommandSuccessResult = applianceCommandSuccessResult;
exports.applianceCommandErrorResult = applianceCommandErrorResult;
exports.pick = pick;
exports.assertExclude = assertExclude;
exports.pairs = pairs;
exports.max = max;
exports.omitNullValuesShallow = omitNullValuesShallow;
exports.sortObjectByKeys = sortObjectByKeys;
exports.areObjectsEqual = areObjectsEqual;
const canonicalize_1 = require("./canonicalize");
const serialization_1 = require("./serialization");
function compareSimple(t1, t2) {
    if (t1 < t2) {
        return -1;
    }
    if (t1 > t2) {
        return 1;
    }
    return 0;
}
function compare(t1, t2, map) {
    const v1 = map(t1);
    const v2 = map(t2);
    for (let i = 0; i < v1.length; i++) {
        const r = compareSimple(v1[i], v2[i]);
        if (r !== 0) {
            return r;
        }
    }
    return 0;
}
function sort(...k) {
    return (t1, t2) => compare(t1, t2, (t) => k.map((keyOrSelector) => (typeof keyOrSelector !== 'function' ? t[keyOrSelector] : keyOrSelector(t))));
}
const sortByStreamId = (a, b) => {
    if (a.streamId === b.streamId)
        return 0;
    if (a.streamId > b.streamId)
        return 1;
    return -1;
};
exports.sortByStreamId = sortByStreamId;
// Credit: https://github.com/ide/backoff
// MIT License
// Copyright (c) 2019 James Ide
// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the "Software"), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:
// The above copyright notice and this permission notice shall be included in all
// copies or substantial portions of the Software.
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
// SOFTWARE.
function computeNextBackoffInterval(initialBackoff, previousRetryCount, { multiplier = 1.5, randomizationFactor = 0.25, minBackoff = initialBackoff, maxBackoff = Infinity } = {}) {
    assert(initialBackoff > 0, `The initial backoff interval must be positive`);
    assert(previousRetryCount >= 0, `The previous retry count must not be negative`);
    assert(multiplier >= 1, `The backoff multiplier must be greater than or equal to 1`);
    assert(0 <= randomizationFactor && randomizationFactor <= 1, `The randomization factor must be between 0 and 1, inclusive`);
    assert(minBackoff >= 0, `The minimum backoff interval must be positive`);
    const nextBackoff = initialBackoff * multiplier ** previousRetryCount;
    // Apply jitter within the negative to positive range of the randomization factor
    const jitterFactor = 1 - randomizationFactor + 2 * randomizationFactor * Math.random();
    return Math.min(Math.max(nextBackoff * jitterFactor, minBackoff), maxBackoff);
}
function assert(cond, message) {
    if (!cond) {
        throw new Error(message);
    }
}
const sleep = (ms) => new Promise((res) => setTimeout(res, ms));
exports.sleep = sleep;
function withTimeout(timeoutMs, fn) {
    return () => {
        const timeout = (0, exports.sleep)(timeoutMs).then(() => Promise.reject(new Error('Timeout')));
        return Promise.race([fn(), timeout]);
    };
}
async function orTimeout(p, timeoutMs) {
    return await Promise.race([
        p,
        (0, exports.sleep)(timeoutMs).then(() => {
            throw new Error(`Timeout ${timeoutMs}ms`);
        }),
    ]);
}
async function retry(op, opt) {
    const { maxRetries, initialBackoff, maxBackoff } = opt;
    let lastError;
    for (let i = 0; i <= maxRetries; i++) {
        try {
            return await op();
        }
        catch (e) {
            lastError = e;
            if (i < maxRetries) {
                const nextSleep = computeNextBackoffInterval(initialBackoff, i, { maxBackoff: maxBackoff | Infinity });
                await (0, exports.sleep)(nextSleep);
                continue;
            }
        }
    }
    throw lastError;
}
function equalsMap(map) {
    return (t1, t2) => {
        return map(t1) === map(t2);
    };
}
function ageSeconds(d, dateNow = new Date()) {
    let date;
    if (typeof d === 'string') {
        date = Date.parse(d);
    }
    else {
        date = d.valueOf();
    }
    return Math.floor((dateNow.valueOf() - date) / 1000);
}
class Deferred {
    constructor() {
        this.promise = new Promise((resolve, reject) => {
            this.resolve = resolve;
            this.reject = reject;
        });
    }
}
exports.Deferred = Deferred;
async function awaitCondition(cond, timeoutMs, intervalMs, throwOnError = false) {
    let v = false;
    const start = Date.now();
    function maybeThrow(p) {
        return p.catch((e) => {
            if (throwOnError) {
                throw e;
            }
            return false;
        });
    }
    while (!(v = await maybeThrow(cond())) && Date.now() - start < timeoutMs) {
        await (0, exports.sleep)(intervalMs);
    }
    return v;
}
function isValidUuid(uuid, version) {
    const uuidRegex = version
        ? new RegExp(`^[0-9a-f]{8}-[0-9a-f]{4}-${version}[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$`, 'i')
        : /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
    return uuidRegex.test(uuid);
}
/** @deprecated Use isValidUuid instead. isValidUuidV4 also allows uuid v1-v5. */
function isValidUuidV4(uuid) {
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
    return uuidRegex.test(uuid);
}
function clone(t) {
    return (0, serialization_1.jsonParse)((0, serialization_1.jsonStringify)(t));
}
function omitProperties(t, ...k) {
    const c = clone(t);
    for (const p of k) {
        delete c[p];
    }
    return c;
}
function applianceCommandSuccessResult(command, result) {
    return {
        type: 'commandResult',
        data: {
            success: true,
            result: result || {},
            command,
        },
    };
}
function applianceCommandErrorResult(command, errorMessage) {
    return {
        type: 'commandResult',
        data: {
            success: false,
            errorMessage,
            command,
        },
    };
}
const equals = (objA, objB) => {
    return (0, canonicalize_1.canonicalize)(objA) == (0, canonicalize_1.canonicalize)(objB);
};
exports.equals = equals;
function pick(t, ...pickKeys) {
    if (!t) {
        return t;
    }
    const o = {};
    for (const k of pickKeys) {
        o[k] = t[k];
    }
    return o;
}
function assertExclude(value, excluded, errorMessage) {
    if (value == excluded) {
        throw new Error(errorMessage || `Unexpected value: ${excluded}`);
    }
    return value;
}
/**
 * Return pairs with one item from each of the sequences
 *
 * @param itemsA itemsA[i] == result[i][0]
 * @param itemsB itemsB[i] == result[i][1]
 */
function pairs(itemsA, itemsB) {
    if (itemsA.length !== itemsB.length) {
        throw new Error(`Can only pair sequences of the same length (${itemsA.length} != ${itemsB.length})`);
    }
    const result = [];
    for (let i = 0; i < itemsA.length; i++) {
        result.push([itemsA[i], itemsB[i]]);
    }
    return result;
}
function max(nums) {
    let leader = Number.NEGATIVE_INFINITY;
    for (const n of nums) {
        if (n > leader) {
            leader = n;
        }
    }
    return leader;
}
const isValidDate = (date) => Object.prototype.toString.call(date) === '[object Date]' && Number.isFinite(date.getTime());
exports.isValidDate = isValidDate;
function omitNullValuesShallow(obj) {
    const returned = {};
    for (const k of Object.keys(obj)) {
        if (obj[k] !== null) {
            returned[k] = obj[k];
        }
    }
    return returned;
}
const toSnakeCase = (input) => {
    return input
        .split(/(?=[A-Z])/)
        .join('_')
        .toLowerCase();
};
exports.toSnakeCase = toSnakeCase;
const notNull = (v) => v !== null;
exports.notNull = notNull;
function sortObjectByKeys(unsorted) {
    return Object.keys(unsorted)
        .sort()
        .reduce((sorted, key) => {
        const value = unsorted[key];
        const isObject = !!(value && typeof value === 'object' && !Array.isArray(value));
        return { ...sorted, [key]: isObject ? sortObjectByKeys(value) : value };
    }, {});
}
function areObjectsEqual(a, b) {
    if (a && b) {
        return JSON.stringify(sortObjectByKeys(a)) === JSON.stringify(sortObjectByKeys(b));
    }
    return !a && !b;
}
//# sourceMappingURL=data:application/json;base64,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